import React, { useState } from 'react';
import { Alert, Button } from 'antd';

/**
 * Human-readable explanation for each AI-scoring failure reason returned by
 * POST /api/v1/projects/:id/gap-analysis/ai-scores (`aiFailureReason` on a
 * fallback row). Commandment IV requires explainability on every AI output —
 * a fallback row's "explanation" is why the model never actually produced it.
 *
 * lesson [[silent-zero-scoring-ai-fallback]] — fallback rows are structurally
 * identical to legitimate zero scores; `aiFailed` / `aiFailureReason` is the
 * only signal, so this map is the single place that turns the code into a
 * sentence an Advisor can act on.
 */
const AI_FAILURE_REASON_TEXT = {
  PARSE_FAILURE: 'AI response was malformed - retry',
  BUDGET_EXCEEDED: 'Daily AI limit reached - it resets at midnight UTC (05:30 IST); retry after that',
  UPSTREAM_UNAVAILABLE: 'Groq API unavailable - retry',
  OTHER: 'AI scoring failed for this question - retry',
};

export function describeAiFailureReason(reason) {
  return AI_FAILURE_REASON_TEXT[reason] || AI_FAILURE_REASON_TEXT.OTHER;
}

/**
 * Top-of-screen warning banner for the Gap Analysis "Review & Validate Scores"
 * step (Task #72 — Engineering Director follow-up on commit a3bfd40).
 *
 * Surfaces the `warnings[]` / `failedBatches[]` the ai-scores endpoint returns
 * on a partial AI failure, plus a "Retry failed batches" action that
 * re-requests scores for only the rows that failed — never a perpetual
 * AI-failed state with no way out (see idempotency note on the parent's
 * retry handler).
 */
export function GapAnalysisAiFailureBanner({
  warnings = [],
  failedBatches = [],
  failedRowCount = 0,
  retrying = false,
  onRetry,
  disabled = false,
  retryLabel = 'Retry failed batches',
}) {
  const [showDetails, setShowDetails] = useState(false);
  const hasFailedBatches = failedBatches.length > 0;
  const hasWarnings = warnings.length > 0;
  if (!hasWarnings && !hasFailedBatches && failedRowCount === 0) return null;

  const canRetry = typeof onRetry === 'function' && (hasFailedBatches || failedRowCount > 0);

  const headline = hasFailedBatches
    ? `${failedBatches.length} of the requested batch${failedBatches.length === 1 ? '' : 'es'} could not be scored by AI`
    : failedRowCount > 0
      ? `${failedRowCount} row${failedRowCount === 1 ? '' : 's'} still carry an AI-failed score`
      : 'AI scoring reported warnings';

  return (
    <Alert
      type="warning"
      showIcon
      message={headline}
      description={
        <div>
          <p style={{ margin: '4px 0 8px' }}>
            These rows show 0/0/0 because the AI call failed, not because the control is
            genuinely scored zero. They are marked with an amber row and an &quot;AI
            failed&quot; tag in the table below - hover the tag for the reason.
          </p>
          {(hasFailedBatches || hasWarnings) && (
            <Button type="link" size="small" style={{ padding: 0, height: 'auto' }} onClick={() => setShowDetails((v) => !v)}>
              {showDetails ? 'Hide details' : 'Show details'}
            </Button>
          )}
          {showDetails && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              {failedBatches.map((fb, i) => (
                <li key={`batch-${fb.startIndex}-${i}`} style={{ fontSize: 12 }}>
                  Batch at index {fb.startIndex} ({fb.size} item{fb.size === 1 ? '' : 's'}) - {fb.reason}
                </li>
              ))}
              {warnings.map((w, i) => (
                <li key={`warn-${i}`} style={{ fontSize: 12 }}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      }
      action={
        canRetry ? (
          // Mirrors every sibling control on this panel (Generate/Save/Validate all pass
          // `disabled: laSubStepReadOnly`) — a Finalized gap-analysis step must stay locked,
          // retry included, not just visually dimmed.
          <Button size="small" type="primary" ghost loading={retrying} disabled={disabled} onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : undefined
      }
    />
  );
}

export default GapAnalysisAiFailureBanner;
